import {
  AllCommunityModule,
  themeQuartz,
  type ColDef,
  type ColumnMovedEvent,
  type GetRowIdParams,
  type ICellRendererParams,
  type IsFullWidthRowParams,
  type OverlayType,
  type RowClassParams,
  type SelectionChangedEvent,
  type SortChangedEvent,
} from "ag-grid-community";
import { AgGridProvider, AgGridReact } from "ag-grid-react";
import classNames from "classnames";
import * as React from "react";

import styles from "./DataTable.module.css";
import { DataTableSkeleton } from "./internal/DataTableSkeleton";
import { ExpandableDetailPanel } from "./internal/ExpandableDetailPanel";
import { ExpandChevron } from "./internal/ExpandChevron";
import { PaginationFooter } from "./internal/PaginationFooter";
import {
  useRowExpansion,
  type ExpandableGridRow,
} from "./internal/useRowExpansion";
import type {
  DataTableLoadingState,
  DataTableSort,
  DataTableSlice,
} from "./useDataTableState";

const AG_GRID_MODULES = [AllCommunityModule];

const SUPPRESSED_OVERLAYS: OverlayType[] = [
  "noRows",
  "loading",
  "noMatchingRows",
];

const DEFAULT_COL_DEF = {
  sortable: false,
  resizable: true,
  lockPinned: true,
  /**
   * The server is the sort authority. This `comparator: () => 0` prevents AG Grid
   * from client-side reordering when a consumer column opts into `sortable: true`.
   * Without it, the displayed order would diverge from the server's ordering.
   */
  comparator: () => 0,
} satisfies ColDef;

const ROW_SELECTION_BASE = {
  mode: "multiRow",
  selectAll: "currentPage",
} as const;

const SELECTION_COLUMN_DEF = {
  suppressMovable: true,
  lockPosition: "left",
  lockPinned: true,
} as const;

const SELECTION_COLUMN_ID = "ag-Grid-SelectionColumn";
const EXPAND_COLUMN_ID = "__expand";

const INTERNAL_COLUMN_IDS = new Set([SELECTION_COLUMN_ID, EXPAND_COLUMN_ID]);

function detailPanelId(rowId: string): string {
  return `data-table-detail-${rowId}`;
}

function getColId<T>(col: ColDef<T>): string {
  return (col.colId ?? col.field) as string;
}

interface DataTableTableProps<TData> extends DataTableSlice {
  /**
   * Whether a page exists after the current `currentPage`
   */
  hasNextPage: boolean;
  /**
   * Current load state. `"foreground"` shows the skeleton overlay; `"background"`
   * keeps existing rows visible while a fetch is in flight; `"idle"` is the
   * resting state. Also disables the next-page button while non-idle so the
   * user can't queue another advance mid-fetch. Default: `"idle"`.
   */
  loading?: DataTableLoadingState;
  /**
   * Column definitions. AG Grid `ColDef` is the public contract — pass raw
   * `ColDef` for full access to AG Grid features, or use `createColumnHelper`
   * for type-safe shortcuts on the common shapes (field accessor, computed
   * value getter, display-only).
   */
  columns: ColDef<TData>[];
  data: TData[];
  /**
   * Pure row → id projection. The first reference passed is captured for the
   * table's lifetime; later references are intentionally ignored so AG Grid
   * sees a single identity strategy. If a consumer needs to switch identity
   * strategies, remount the table (e.g. via a `key` prop) — passing a new
   * function will not take effect.
   */
  getRowId: (row: TData) => string;
  noRowsContent: React.ReactNode;
  /**
   * Total rows across all pages, surfaced in the pagination footer's
   * `1-25 of N` label. When `undefined`, the count label is hidden.
   */
  totalRowCount?: number | undefined;
  /**
   * Show the row-selection checkbox column. Defaults to `false`. The current
   * selection is read from `useDataTableState().tableState.selectedIds` — this
   * prop only toggles the UI affordance, not the selection state itself.
   */
  selectionEnabled?: boolean;
  /**
   * Row expansion config. Pass an object to enable per-row detail panels —
   * `renderRow` is invoked with the row data and its return value is rendered
   * inside the expanded panel. Omit to disable expansion entirely.
   */
  expansion?: { renderRow: (row: TData) => React.ReactNode };
  className?: string;
}

export function DataTableTable<TData>(props: DataTableTableProps<TData>) {
  const {
    initialSort,
    initialColumnOrder,
    onSortChange,
    onColumnOrderChange,
    onSelectionChange,
    pageSize,
    onPageSizeChange,
    currentPage,
    nextPage,
    prevPage,
    hasNextPage,
    canGetPrevPage,
    loading = "idle",
    columns,
    data,
    getRowId,
    noRowsContent,
    totalRowCount,
    selectionEnabled = false,
    expansion,
    className,
  } = props;

  const showExpansion = expansion !== undefined;
  const renderExpandedRow = expansion?.renderRow;

  // `getRowId` is a pure row→id projection; capture the first reference so
  // AG Grid sees one identity strategy. Later changes are intentionally ignored
  // — if a consumer needs a different identity strategy, remount the table.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableGetRowId = React.useMemo(() => getRowId, []);

  // Snapshot of persisted state at mount, shaped for AG Grid's `initialState`
  // prop. AG Grid only reads `initialState` once on first render, so the hook
  // remains the source of truth: subsequent user sorts/resizes/moves flow back
  // through their respective handlers, and AG Grid never re-applies this snapshot.
  const [initialGridState] = React.useState(() => {
    const sortState:
      | { sortModel: { colId: string; sort: DataTableSort["direction"] }[] }
      | undefined = initialSort
      ? {
          sortModel: [
            { colId: initialSort.columnId, sort: initialSort.direction },
          ],
        }
      : undefined;

    const consumerColIds = columns.map(getColId);
    const persisted = initialColumnOrder ?? [];
    const hasPersistedOrder = persisted.length > 0;

    let orderedConsumer: string[];
    if (hasPersistedOrder) {
      const knownIds = new Set(consumerColIds);
      const used = new Set<string>();
      orderedConsumer = [];
      for (const id of persisted) {
        if (knownIds.has(id) && !used.has(id)) {
          orderedConsumer.push(id);
          used.add(id);
        }
      }
      for (const id of consumerColIds) {
        if (!used.has(id)) {
          orderedConsumer.push(id);
          used.add(id);
        }
      }
    } else {
      orderedConsumer = consumerColIds;
    }

    // AG Grid expects the full set of colIds. Pinned-left internal columns
    // come first — expand before selection — followed by consumer cols.
    let columnOrder: { orderedColIds: string[] } | undefined;
    if (showExpansion || selectionEnabled || hasPersistedOrder) {
      const fullOrder: string[] = [];
      if (showExpansion) {
        fullOrder.push(EXPAND_COLUMN_ID);
      }
      if (selectionEnabled) {
        fullOrder.push(SELECTION_COLUMN_ID);
      }
      fullOrder.push(...orderedConsumer);
      columnOrder = { orderedColIds: fullOrder };
    }

    if (!sortState && !columnOrder) {
      return undefined;
    }
    return {
      ...(sortState ? { sort: sortState } : {}),
      ...(columnOrder ? { columnOrder } : {}),
      partialColumnState: true,
    };
  });

  const { displayRows, toggle: toggleExpansion } = useRowExpansion<TData>({
    data,
    getRowId: stableGetRowId,
  });

  const rowData: ExpandableGridRow<TData>[] = showExpansion
    ? displayRows
    : (data as ExpandableGridRow<TData>[]);

  const wrappedGetRowId = React.useCallback(
    (params: GetRowIdParams<ExpandableGridRow<TData>>) => {
      const id = stableGetRowId(params.data as TData);
      return params.data.__detail === true ? `${id}__detail` : id;
    },
    [stableGetRowId],
  );

  const finalColumns = React.useMemo<ColDef<ExpandableGridRow<TData>>[]>(() => {
    /**
     * This is safe because detail rows are intercepted by
     * `isFullWidthRow` and rendered via `fullWidthCellRenderer` below — they
     * never reach the per-column `cellRenderer` / `valueGetter` / etc., so
     * `params.data` in consumer callbacks is always a real `TData`.
     *
     * Considered wrapping TData with ExpandableGridRow but every Ag-grid call site
     * would be impacted so left it be.
     */
    const consumerCols = columns as ColDef<ExpandableGridRow<TData>>[];
    if (!showExpansion) {
      return consumerCols;
    }
    const expandColumn: ColDef<ExpandableGridRow<TData>> = {
      colId: EXPAND_COLUMN_ID,
      width: 40,
      pinned: "left",
      sortable: false,
      resizable: false,
      suppressMovable: true,
      lockPosition: "left",
      cellClass: styles.expandCell,
      onCellClicked: (event) => {
        const row = event.data;
        if (!row || row.__detail) {
          return;
        }
        toggleExpansion(stableGetRowId(row as TData));
      },
      suppressKeyboardEvent: (params) => {
        if (params.event.key !== "Enter") {
          return false;
        }
        const row = params.data;
        toggleExpansion(stableGetRowId(row as TData));
        return true;
      },
      valueGetter: (params) => params.data?.__expanded ?? false,
      cellRenderer: (params: ICellRendererParams<ExpandableGridRow<TData>>) => {
        const row = params.data;
        if (!row || row.__detail) {
          return null;
        }
        const isExpanded = Boolean(params.value);
        const rowId = stableGetRowId(row as TData);
        return (
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-controls={detailPanelId(rowId)}
            aria-label={isExpanded ? "Collapse row" : "Expand row"}
            className={styles.expandButton}
          >
            <ExpandChevron isExpanded={isExpanded} />
          </button>
        );
      },
    };
    return [expandColumn, ...consumerCols];
  }, [columns, showExpansion, toggleExpansion, stableGetRowId]);

  const handleColumnMoved = React.useCallback(
    (event: ColumnMovedEvent<ExpandableGridRow<TData>>) => {
      if (!event.finished) {
        return;
      }
      // AG Grid also fires columnMoved while hydrating `initialState.columnOrder`.
      // This only invokes the callback on user-initiated re-ordering.
      if (
        event.source !== "uiColumnDragged" &&
        event.source !== "uiColumnMoved"
      ) {
        return;
      }
      const order = event.api
        .getColumnState()
        .map((c) => c.colId)
        .filter(
          (id): id is string =>
            id !== undefined && !INTERNAL_COLUMN_IDS.has(id),
        );
      onColumnOrderChange(order);
    },
    [onColumnOrderChange],
  );

  const handleSortChanged = React.useCallback(
    (event: SortChangedEvent<ExpandableGridRow<TData>>) => {
      if (event.source !== "uiColumnSorted") {
        return;
      }

      const active = event.api
        .getColumnState()
        .find((c) => c.sort !== null && c.sort !== undefined && c.colId);
      if (!active?.colId) {
        onSortChange(null);
        return;
      }
      onSortChange({
        columnId: active.colId,
        direction: active.sort === "asc" ? "asc" : "desc",
      });
    },
    [onSortChange],
  );

  const rowSelectionConfig = React.useMemo(() => {
    if (!selectionEnabled) {
      return undefined;
    }
    if (showExpansion) {
      return {
        ...ROW_SELECTION_BASE,
        isRowSelectable: (node: { data?: ExpandableGridRow<TData> }) =>
          !node.data?.__detail,
      } as const;
    }
    return ROW_SELECTION_BASE;
  }, [selectionEnabled, showExpansion]);

  const handleSelectionChanged = React.useCallback(
    (event: SelectionChangedEvent<ExpandableGridRow<TData>>) => {
      const ids = new Set(
        event.api
          .getSelectedRows()
          .filter((row) => !row.__detail)
          .map((row) => stableGetRowId(row as TData)),
      );
      onSelectionChange(ids);
    },
    [onSelectionChange, stableGetRowId],
  );

  const fullWidthCellRenderer = React.useMemo(() => {
    if (!showExpansion || !renderExpandedRow) {
      return undefined;
    }
    const render = renderExpandedRow;
    return function FullWidthDetail(
      params: ICellRendererParams<ExpandableGridRow<TData>>,
    ) {
      const data = params.data;
      if (!data) {
        return null;
      }
      const { __detail: _detail, __expanded: _expanded, ...rest } = data;
      const consumerRow = rest as TData;
      return (
        <ExpandableDetailPanel
          cellParams={params as ICellRendererParams<unknown>}
          id={detailPanelId(stableGetRowId(consumerRow))}
        >
          {render(consumerRow)}
        </ExpandableDetailPanel>
      );
    };
  }, [showExpansion, renderExpandedRow, stableGetRowId]);

  const isFullWidthRow = React.useCallback(
    (params: IsFullWidthRowParams<ExpandableGridRow<TData>>) =>
      params.rowNode.data?.__detail === true,
    [],
  );

  const getRowClass = React.useCallback(
    (params: RowClassParams<ExpandableGridRow<TData>>) => {
      if (params.data?.__detail) {
        return styles.detailRow;
      }
      return showExpansion ? styles.expandableRow : "";
    },
    [showExpansion],
  );

  const showSkeleton = loading === "foreground";
  const showEmptyState = !showSkeleton && data.length === 0;

  return (
    <AgGridProvider modules={AG_GRID_MODULES}>
      <div className={classNames(styles.frame, className)}>
        <div className={styles.gridFrame}>
          <div className={styles.grid}>
            <AgGridReact<ExpandableGridRow<TData>>
              theme={themeQuartz}
              rowData={rowData}
              columnDefs={finalColumns}
              defaultColDef={DEFAULT_COL_DEF}
              initialState={initialGridState}
              getRowId={wrappedGetRowId}
              getRowClass={showExpansion ? getRowClass : undefined}
              isFullWidthRow={showExpansion ? isFullWidthRow : undefined}
              fullWidthCellRenderer={fullWidthCellRenderer}
              rowSelection={rowSelectionConfig}
              selectionColumnDef={SELECTION_COLUMN_DEF}
              enableCellTextSelection
              suppressColumnMoveAnimation
              suppressMultiSort
              onSortChanged={handleSortChanged}
              onColumnMoved={handleColumnMoved}
              onSelectionChanged={
                selectionEnabled ? handleSelectionChanged : undefined
              }
              suppressOverlays={SUPPRESSED_OVERLAYS}
            />
          </div>
          {showSkeleton ? (
            <div className={styles.skeletonOverlay}>
              <DataTableSkeleton
                rowCount={12}
                columnCount={columns.length}
                hasSelectionColumn={selectionEnabled}
              />
            </div>
          ) : null}
          {showEmptyState ? (
            <div className={styles.emptyOverlay}>{noRowsContent}</div>
          ) : null}
        </div>
        <PaginationFooter
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          currentPage={currentPage}
          nextPage={nextPage}
          prevPage={prevPage}
          hasNextPage={hasNextPage}
          canGetPrevPage={canGetPrevPage}
          totalRowCount={totalRowCount}
          loading={loading}
        />
      </div>
    </AgGridProvider>
  );
}
